import { Control, Controller, UseFormResetField, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// local imports
import avatarIcon from 'assets/icons/avatar.svg'
import branchList from 'assets/icons/list-branch.svg'
import { ICoiffeurSearchForm, IFilteredCoiffeurList, IListCoiffeurSaloonSpec } from 'types/CoiffeurType'
import { uniqByProp } from 'utils/Miscellaneous'
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import CoiffeurPhoto from './CoiffeurPhoto'
import { DropdownWrapper, PriceInfo, StyledCustomDropdown, StyledDropdownItems } from './styled'

interface ICoiffeurSearchFormProps {
  control: Control<ICoiffeurSearchForm>
  watch: UseFormWatch<ICoiffeurSearchForm>
  resetField: UseFormResetField<any>
  salonSpecs: IListCoiffeurSaloonSpec[]
  coiffeurList: IFilteredCoiffeurList[] | undefined
}

function valueTemplate(options: any, props: any) {
  return <div className="selectedVal">{options?.name || props?.placeholder}</div>
}

function coiffeurValueTemplate(options: IFilteredCoiffeurList, props: any) {
  if (!options) return <>{props?.placeholder}</>
  const label = `${options?.name}  ${options?.surname}`
  return <>{label || props?.placeholder}</>
}

function coiffeurOptTemplate(options: IFilteredCoiffeurList) {
  return (
    <StyledDropdownItems>
      <div className="coiffeur-photo">
        <CoiffeurPhoto coiffeurEmployeeId={options.employeeId || 0} IsAbsolute={false} size={[40, 40]} />
      </div>

      <div className="coiffeur-info">
        <div className="coiffeur-name-specialty">
          <span className="coiffeur-name">
            {options.name} {options.surname}
          </span>
        </div>
      </div>
    </StyledDropdownItems>
  )
}

/* #################### Search Form Component #################### */
export default function CoiffeurSearchForm({
  control,
  resetField,
  watch,
  coiffeurList,
  salonSpecs,
}: ICoiffeurSearchFormProps) {
  const { t } = useTranslation()
  const mainServiceVal = watch('mainServiceId')
  const subServiceVal = watch('subServiceId')
  const coiffeurVal = watch('coiffeur')

  /* Main - Sub Services Options */
  const mainServices = salonSpecs.map((ss) => ss.coiffeurSubService.coiffeurMainService)
  const mainServiceOptions = uniqByProp('id')(mainServices)
  const subServices = salonSpecs
    .filter((ss) => ss.coiffeurSubService.coiffeurMainService.id === mainServiceVal)
    .map(({ coiffeurSubService, price, priceMax }) => ({
      ...coiffeurSubService,
      price: price,
      priceMax: priceMax,
    }))

  /* Price Info variables */
  const price = subServices.find((s) => s.id === subServiceVal)?.price
  const priceMax = subServices.find((s) => s.id === subServiceVal)?.priceMax
  const subServiceName = subServices.find((s) => s.id === subServiceVal)?.name

  return (
    <div className="d-flex flex-column gap-2" id="searchFormWrapper">
      {/* Main Service */}
      <Controller
        name="mainServiceId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper id="coiffeurMainService">
            <label className="autocomplete-title">{t('input-texts.main-service-hair')}</label>
            <img className="autocomplete-icon p-input-icon-left" src={branchList} alt="logo" width={18} height={18} />

            <StyledCustomDropdown
              id="mainServiceId"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="mainServiceId"
              placeholder={t('input-texts.choose-main-service')}
              optionLabel="name"
              optionValue="id"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={mainServiceOptions}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('subServiceId')
                resetField('coiffeur')
              }}
            />
          </DropdownWrapper>
        )}
      />

      {/* Sub Service */}
      {!!mainServiceVal && (
        <Controller
          name="subServiceId"
          control={control}
          render={({ field }) => (
            <DropdownWrapper>
              <label className="autocomplete-title">{t('input-texts.sub-service-hair')}</label>
              <img className="autocomplete-icon p-input-icon-left" src={branchList} alt="logo" width={18} height={18} />
              <StyledCustomDropdown
                id="subServiceId"
                emptyMessage={t('most-used-words.option-not-found')}
                name="subServiceId"
                placeholder={t('input-texts.choose-sub-service')}
                optionLabel="name"
                optionValue="id"
                valueTemplate={(opt, props) => valueTemplate(opt, props)}
                options={subServices}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.value)
                  resetField('coiffeur')
                }}
              />
            </DropdownWrapper>
          )}
        />
      )}

      {!!subServiceVal ? (
        <PriceInfo>
          <i className="pi pi-info-circle" />
          {!!price ? (
            <>
              {!!priceMax ? (
                <span>
                  “{subServiceName}” {t('most-used-words.process-fee-range')} :
                  <span className="price">
                    {price}₺ - {priceMax}₺
                  </span>
                </span>
              ) : (
                <span>
                  “{subServiceName}” {t('most-used-words.process-fee')} : <span className="price"> {price}₺</span>
                </span>
              )}
            </>
          ) : (
            <span>
              “{subServiceName}” {t('most-used-words.process-fee')} :
              <span className="price"> {t('most-used-words.price-free')}</span>
            </span>
          )}
        </PriceInfo>
      ) : null}

      {/* Coiffeur */}
      {!!subServiceVal && (
        <Controller
          name="coiffeur"
          control={control}
          render={({ field }) => (
            <DropdownWrapper>
              <img className="autocomplete-icon p-input-icon-left" src={avatarIcon} alt="logo" width={20} height={20} />
              <StyledCustomDropdown
                id="coiffeurDropdown"
                name="coiffeur"
                className="coiffeur-dropdown"
                emptyMessage={t('most-used-words.option-not-found')}
                appendTo="self"
                placeholder={t('input-texts.choose-coiffeur')}
                optionLabel="name"
                options={coiffeurList}
                valueTemplate={coiffeurValueTemplate}
                itemTemplate={coiffeurOptTemplate}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
            </DropdownWrapper>
          )}
        />
      )}

      {/* Reservation Date */}
      {!!coiffeurVal && (
        <Controller
          name="reservationDate"
          control={control}
          render={({ field }) => (
            <DatePickerIcon
              onFocus={() => {}}
              transitionOptions={{ disabled: true, addEndListener: (document.body, () => {}) }}
              touchUI
              appendTo={null}
              selectOtherMonths
              showOtherMonths
              disabledDays={[]}
              idName="coiffeurReservationDateForm"
              defaultDate={field.value}
              minDate={DateUtil.previousMonth()}
              maxDate={DateUtil.futureMonth()}
              onSelectedDateChange={(e) => field.onChange(e.value as Date)}
              label={t('input-texts.reservation-date')}
            />
          )}
        />
      )}
    </div>
  )
}
